// Adaptive gear catalog — IronLimb Pillar Session C.
// Equipment specifically supporting adaptive users (chronic pain, amputee,
// mobility-limited). Surfaced in the GearVault on the Library page.
//
// `variants` matches the ids in data/prosthesisVariants.js so users can
// filter to gear that matches their selected variants. Use 'all' to mark
// gear that's universally useful (chronic-pain support, etc.).
//
// `affiliateUrl` is null until each partner is signed. Card shows
// "Affiliate link coming soon" placeholder; once URL is set, card becomes
// a working external link.

export const ADAPTIVE_GEAR_CATEGORIES = [
  { id: 'prosthesis-accessory', label: 'Prosthesis accessory' },
  { id: 'mobility-aid', label: 'Mobility aid' },
  { id: 'seated-training', label: 'Seated training' },
  { id: 'one-arm', label: 'One-arm / grip' },
  { id: 'compression', label: 'Compression' },
  { id: 'pain-management', label: 'Pain management' },
  { id: 'adaptive-cardio', label: 'Adaptive cardio' },
];

export const ADAPTIVE_GEAR = [
  {
    id: 'residual-limb-sock-pack',
    name: 'Residual-limb sock pack',
    category: 'prosthesis-accessory',
    variants: ['transtibial', 'transfemoral'],
    priceRange: '$30–80',
    blurb: 'Multi-ply socks let you fine-tune socket fit through the day as the residual limb changes volume.',
    affiliateUrl: null,
  },
  {
    id: 'silicone-liner',
    name: 'Prosthetic liner (silicone)',
    category: 'prosthesis-accessory',
    variants: ['transtibial', 'transfemoral', 'knee-disarticulation'],
    priceRange: '$200–600',
    blurb: 'Silicone liner that distributes pressure across the residual limb. The starting point if your socket feels harsh.',
    affiliateUrl: null,
  },
  {
    id: 'sport-blade-adapter',
    name: 'Sport-blade socket adapter',
    category: 'prosthesis-accessory',
    variants: ['sport-blade'],
    priceRange: '$150–400',
    blurb: 'Swappable adapter that mates a day-prosthesis socket to a running blade or sport pylon.',
    affiliateUrl: null,
  },
  {
    id: 'single-arm-grip-hook',
    name: 'Single-arm grip strap / hook attachment',
    category: 'one-arm',
    variants: ['transradial', 'transhumeral', 'shoulder-disarticulation', 'partial-hand'],
    priceRange: '$25–80',
    blurb: 'Wrist or stump cuff with a hook or D-ring — clips into dumbbells, cables, kettlebells. Frees you from grip dependence.',
    affiliateUrl: null,
  },
  {
    id: 'wheelchair-handrim-gloves',
    name: 'Wheelchair handrim gloves',
    category: 'mobility-aid',
    variants: ['hip-disarticulation', 'no-prosthesis'],
    priceRange: '$30–60',
    blurb: 'Padded gloves designed for push-rim contact. Prevent calluses and improve power transfer.',
    affiliateUrl: null,
  },
  {
    id: 'adaptive-bands-anchor-loops',
    name: 'Adaptive resistance bands with anchor loops',
    category: 'seated-training',
    variants: ['all'],
    priceRange: '$25–55',
    blurb: 'Bands with built-in carabiner loops — anchor to a chair, door, wheelchair frame. Enable seated, single-arm, or single-leg work.',
    affiliateUrl: null,
  },
  {
    id: 'universal-cuff-cable-handle',
    name: 'Universal-cuff cable handle',
    category: 'one-arm',
    variants: ['transradial', 'transhumeral', 'shoulder-disarticulation', 'partial-hand', 'hip-disarticulation', 'no-prosthesis'],
    priceRange: '$15–35',
    blurb: 'Velcro cuff that wraps the forearm or wrist and clips to any cable / band — replaces hand-grip on rows, pulldowns, and curls.',
    affiliateUrl: null,
  },
  {
    id: 'microprocessor-knee-dock',
    name: 'Microprocessor-knee charging dock',
    category: 'prosthesis-accessory',
    variants: ['microprocessor-knee'],
    priceRange: '$80–180',
    blurb: 'Travel-ready dock for C-Leg, X3, Genium, Rheo. Skip the bag-of-cables ritual.',
    affiliateUrl: null,
  },
  {
    id: 'residual-limb-compression',
    name: 'Residual-limb compression sleeve',
    category: 'compression',
    variants: ['transtibial', 'transfemoral', 'knee-disarticulation', 'hip-disarticulation', 'transradial', 'transhumeral', 'shoulder-disarticulation'],
    priceRange: '$30–90',
    blurb: 'Graduated-pressure sleeve to manage end-of-day swelling and shape conditioning.',
    affiliateUrl: null,
  },
  {
    id: 'partial-foot-insole',
    name: 'Partial-foot insole / prosthesis sock',
    category: 'prosthesis-accessory',
    variants: ['partial-foot'],
    priceRange: '$25–120',
    blurb: 'Closes the volume gap inside a standard shoe. Reduces piston motion and skin shear.',
    affiliateUrl: null,
  },
  {
    id: 'forearm-crutches-lightweight',
    name: 'Lightweight forearm crutches',
    category: 'mobility-aid',
    variants: ['no-prosthesis', 'hip-disarticulation', 'transfemoral'],
    priceRange: '$80–250',
    blurb: 'Carbon-fiber or titanium forearm crutches. Significantly lighter than aluminum standard issue. Day-of-amputation users especially feel the difference.',
    affiliateUrl: null,
  },
  {
    id: 'shower-transfer-bench',
    name: 'Bath / shower transfer bench',
    category: 'mobility-aid',
    variants: ['no-prosthesis', 'hip-disarticulation', 'transtibial', 'transfemoral', 'knee-disarticulation'],
    priceRange: '$70–180',
    blurb: 'Sliding bench so you can shower without putting the prosthesis on. Essential during socket-fit issues or hot-day swelling.',
    affiliateUrl: null,
  },
  {
    id: 'mobility-wedges-blocks',
    name: 'Adaptive yoga blocks + mobility wedges',
    category: 'seated-training',
    variants: ['all'],
    priceRange: '$25–60',
    blurb: 'Wider, denser blocks + slanted wedges. Build leverage for floor work when one limb won\'t bear weight.',
    affiliateUrl: null,
  },
  {
    id: 'tens-unit',
    name: 'TENS unit (OTC)',
    category: 'pain-management',
    variants: ['all'],
    priceRange: '$40–120',
    blurb: 'Over-the-counter electrical stimulation unit commonly used to manage chronic pain and phantom-limb sensations. Talk to your clinician about placement and intensity.',
    affiliateUrl: null,
  },
  {
    id: 'adaptive-cycling-pedals',
    name: 'Adaptive cycling pedal attachments',
    category: 'adaptive-cardio',
    variants: ['transtibial', 'transfemoral', 'partial-foot', 'knee-disarticulation'],
    priceRange: '$40–180',
    blurb: 'Toe-cage extenders, prosthesis-foot pedal cleats, and hand-cycle adapters — match your variant to the right hardware.',
    affiliateUrl: null,
  },
];

// Returns gear filtered by an array of variant ids. If `variantFilter` is
// empty, returns everything. Gear with `variants: ['all']` always passes.
export const filterAdaptiveGear = (variantFilter) => {
  if (!variantFilter || variantFilter.length === 0) return ADAPTIVE_GEAR;
  return ADAPTIVE_GEAR.filter((g) => {
    if (g.variants.includes('all')) return true;
    return g.variants.some((v) => variantFilter.includes(v));
  });
};
